/**
 * Exiting Employees Hook
 *
 * Loads the manager's team members whose employment ends within the next N days
 * (default 30), for the "Exiting employees" metric + drill-down modal on the
 * Manager Dashboard.
 *
 * Source: `/data/EmployeesV2`, read per managed employee (mirrors
 * `useTeamBirthdays`'s approach) rather than one broad collection filter.
 * A `$filter` comparison across the whole EmployeesV2 collection
 * (`EmploymentEndDate ge ... and ... le ...`, unscoped by PersonnelNumber)
 * reliably 400s in this D365 environment — a known "SQL subquery" limitation
 * on this composite entity — and `employeeService.getEmployees` silently
 * swallows that 400 into an empty result, which is why the card always showed
 * zero. Fetching one employee at a time via `getEmployeeByNumber` (filtered by
 * `PersonnelNumber eq`) is the query shape already proven to work everywhere
 * else in the portal (`useTeamMemberDetail`, `useTeamBirthdays`), so the
 * "next N days" window is applied client-side instead.
 *
 * Members are mapped into the same card shape the rest of the Manager Portal
 * uses — { id, personnelNumber, name, role, status, employmentEndDate } — so
 * selecting one can open the shared TeamMemberDetailModal.
 *
 * Returns:
 *   - exitingEmployees: Array<{ id, personnelNumber, name, role, status, employmentEndDate }>
 *   - count: number
 *   - isLoading: boolean
 *   - error: Error | null
 */
import { useQuery } from "react-query";
import { employeeService } from "../../api/services";
import useManagerStatus from "./useManagerStatus";

const WINDOW_DAYS = 30;

/** D365 uses placeholder dates for "unset" values; treat those as no date. */
const isPlaceholderDate = (dateStr) =>
  !dateStr ||
  dateStr.startsWith("1900-01-01") ||
  new Date(dateStr).getFullYear() >= 2100;

/**
 * Inclusive [from, to] window for the exit period: from the start of today
 * through the end of the day `days` from now. `referenceDate` is injectable
 * for testing; defaults to now.
 */
const exitWindow = (days, referenceDate = new Date()) => {
  const from = new Date(referenceDate);
  from.setHours(0, 0, 0, 0);

  const to = new Date(referenceDate);
  to.setDate(to.getDate() + days);
  to.setHours(23, 59, 59, 999);

  return { from, to };
};

/**
 * Map a raw EmployeesV2 record into the card shape used across the portal.
 */
const mapExiting = (employee, personnelNumber) => {
  const name =
    employee?.Name ||
    employee?.displayName ||
    employee?.fullName ||
    employee?.DisplayName ||
    personnelNumber;

  const role =
    employee?.PositionTitle ||
    employee?.PrimaryPositionTitle ||
    employee?.Title ||
    "—";

  const isInactive =
    employee?.IsActive === false ||
    employee?.EmploymentStatus === "Inactive" ||
    employee?.WorkerStatus === "Inactive";

  const employmentEndDate = employee?.EmploymentEndDate || null;

  return {
    id: personnelNumber,
    personnelNumber,
    name,
    role,
    status: isInactive ? "On Leave" : "Active",
    employmentEndDate: isPlaceholderDate(employmentEndDate)
      ? null
      : employmentEndDate,
  };
};

export default function useExitingEmployees(days = WINDOW_DAYS) {
  const {
    managedPersonnelNumbers,
    isLoading: managerStatusLoading,
    error: managerStatusError,
  } = useManagerStatus();

  const {
    data: exitingEmployees,
    isLoading: exitingLoading,
    error: exitingError,
  } = useQuery(
    ["exitingEmployees", days, managedPersonnelNumbers],
    async () => {
      const { from, to } = exitWindow(days);
      console.log(
        `🚪 Fetching exiting employees between ${from.toISOString()} and ${to.toISOString()} for ${managedPersonnelNumbers.length} managed person(s)`
      );

      // Fetch each managed employee in parallel; skip any that fail so one bad
      // record doesn't blank the whole list (mirrors useTeamBirthdays).
      const results = await Promise.all(
        managedPersonnelNumbers.map(async (personnelNumber) => {
          try {
            const res = await employeeService.getEmployeeByNumber(
              personnelNumber
            );
            const employee = res.data?.value?.[0] || null;
            return mapExiting(employee, personnelNumber);
          } catch (err) {
            console.error(
              `❌ Failed to fetch employee for ${personnelNumber}:`,
              err
            );
            return null;
          }
        })
      );

      return results
        .filter(Boolean)
        .filter((m) => {
          if (!m.employmentEndDate) return false;
          const endDate = new Date(m.employmentEndDate);
          return endDate >= from && endDate <= to;
        })
        .sort(
          (a, b) => new Date(a.employmentEndDate) - new Date(b.employmentEndDate)
        );
    },
    {
      // Only meaningful once we know who the manager manages.
      enabled: managedPersonnelNumbers.length > 0,
      retry: 2,
      staleTime: 5 * 60 * 1000,
    }
  );

  return {
    exitingEmployees: exitingEmployees ?? [],
    count: exitingEmployees?.length ?? 0,
    isLoading:
      managerStatusLoading ||
      (managedPersonnelNumbers.length > 0 && exitingLoading),
    error: managerStatusError || exitingError || null,
  };
}
